import { memo, useEffect, useLayoutEffect, useRef } from "react";
import { enableChartInteractions, vnodeToDom } from "@broadpaper/renderer";
import type { LayoutResult } from "@broadpaper/renderer";

interface PageViewProps {
  layout: LayoutResult;
  index: number;
  version: number;
  visible: boolean;
  zoom: number;
  /** Charts respond to a pointer and to the keyboard. */
  interactive?: boolean;
}

/**
 * Mounts one rendered page (a VNode from the shared pipeline) into the DOM.
 * The page content is not React-managed; React only owns the slot.
 */
export const PageView = memo(function PageView({ layout, index, version, visible, zoom, interactive }: PageViewProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { width, height } = layout.page;
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!visible) {
      el.replaceChildren();
      return;
    }
    const v = layout.renderPage(index);
    el.replaceChildren(vnodeToDom(v, el.ownerDocument));
  }, [layout, index, version, visible]);

  // After the page is in the DOM, not during: the interaction layer reads the
  // marks' real boxes, and it has to be torn down and rebuilt whenever the page
  // content is replaced, which is every layout.
  useEffect(() => {
    const el = ref.current;
    if (!el || !visible || !interactive) return;
    return enableChartInteractions(el);
  }, [layout, index, version, visible, interactive]);
  return (
    <div className={`bpe-page-slot ${visible ? "" : "bpe-page-slot--placeholder"}`} style={{ width: width * zoom, height: height * zoom, marginBottom: 28 }} data-page-slot={index}>
      <span className="bpe-page-number" aria-hidden="true">
        Page {index + 1} of {layout.paged.totalPages}
      </span>
      <div ref={ref} className="bpe-page__zoom" style={{ zoom, width, height }} />
    </div>
  );
});
